import { useMemo } from 'react';
import {
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { EntityFormDialog } from '../../components/dialogs/EntityFormDialog';
import {
  useCreateMachine,
  useEditMachine,
  useMe,
  useOperatorsList,
  selectableSites,
  useSitesList,
  useSuppliersList,
  useUsersList,
} from '../../api/queries';
import type { MachineSummary } from '../../api/types';

const PROTOCOLS = [
  { value: 'sas_602',  label: 'SAS 6.02' },
  { value: 'glc_pulse', label: 'GLC pulse' },
  { value: 'glc_serial', label: 'GLC serial' },
  { value: 'mib',       label: 'MIB' },
  { value: 'other',     label: 'Other' },
];

const CASHLESS_MODES = [
  { value: 'monitoring_only',     label: 'Monitoring only' },
  { value: 'aft_money_on',        label: 'AFT money-on' },
  { value: 'aft_money_on_off',    label: 'AFT money-on + off' },
  { value: 'tito',                label: 'TITO' },
];

const schema = z.object({
  operatorId: z.string().uuid().or(z.literal('')).optional(),
  siteId: z.string().uuid('Site is required'),
  assetNumber: z.string().trim().min(1, 'Required').max(40),
  make: z.string().trim().min(1, 'Required').max(80),
  model: z.string().trim().min(1, 'Required').max(80),
  gameName: z.string().trim().max(120).optional()
              .or(z.literal('').transform(() => undefined)),
  protocol: z.string().min(1, 'Required'),
  denominationCents: z.coerce.number().int().min(1, 'Must be ≥ 1'),
  rtpBasisPoints: z.coerce.number().int().min(0).max(10_000).optional()
                    .or(z.literal('').transform(() => undefined)),
  ownershipKind: z.enum(['operator', 'supplier', 'site_owner']),
  ownerSupplierId: z.string().uuid().or(z.literal('')).optional(),
  ownerSiteOwnerUserId: z.string().uuid().or(z.literal('')).optional(),
  cashlessMode: z.string().min(1, 'Required'),
}).superRefine((v, ctx) => {
  if (v.ownershipKind === 'supplier' && !v.ownerSupplierId) {
    ctx.addIssue({ code: 'custom', path: ['ownerSupplierId'],
      message: 'Required when ownership is supplier.' });
  }
  if (v.ownershipKind === 'site_owner' && !v.ownerSiteOwnerUserId) {
    ctx.addIssue({ code: 'custom', path: ['ownerSiteOwnerUserId'],
      message: 'Required when ownership is site owner.' });
  }
});

export type MachineFormValues = z.infer<typeof schema>;

export type MachineFormDialogProps = {
  open: boolean;
  mode: 'create' | 'edit';
  machine?: MachineSummary;
  onClose: () => void;
};

export function MachineFormDialog({ open, mode, machine, onClose }: MachineFormDialogProps) {
  const me = useMe();
  const isArms = me.data?.is_arms ?? false;
  const create = useCreateMachine();
  const edit = useEditMachine(machine?.id ?? '');

  const initial: MachineFormValues = mode === 'edit' && machine
    ? {
        operatorId:           machine.operatorId,
        siteId:               machine.siteId,
        assetNumber:          machine.assetNumber,
        make:                 machine.make,
        model:                machine.model,
        gameName:             machine.gameName ?? '',
        protocol:             machine.protocol,
        denominationCents:    machine.denominationCents,
        rtpBasisPoints:       machine.rtpBasisPoints ?? undefined,
        ownershipKind:        (machine.ownershipKind as 'operator' | 'supplier' | 'site_owner'),
        ownerSupplierId:      machine.ownerSupplierId ?? '',
        ownerSiteOwnerUserId: machine.ownerSiteOwnerUserId ?? '',
        cashlessMode:         machine.cashlessMode,
      }
    : {
        operatorId:           isArms ? '' : (me.data?.operator_id ?? ''),
        siteId:               '',
        assetNumber:          '',
        make:                 '',
        model:                '',
        gameName:             '',
        protocol:             'sas_602',
        denominationCents:    1,
        rtpBasisPoints:       undefined,
        ownershipKind:        'operator',
        ownerSupplierId:      '',
        ownerSiteOwnerUserId: '',
        cashlessMode:         'monitoring_only',
      };

  const submit = async (values: MachineFormValues) => {
    if (mode === 'edit') {
      await edit.mutateAsync({
        siteId:            values.siteId,
        make:              values.make,
        model:             values.model,
        gameName:          values.gameName ?? null,
        protocol:          values.protocol,
        denominationCents: values.denominationCents,
        rtpBasisPoints:    values.rtpBasisPoints ?? null,
        cashlessMode:      values.cashlessMode,
      });
    } else {
      await create.mutateAsync({
        operatorId:           values.operatorId || null,
        siteId:               values.siteId,
        assetNumber:          values.assetNumber,
        make:                 values.make,
        model:                values.model,
        gameName:             values.gameName ?? null,
        protocol:             values.protocol,
        denominationCents:    values.denominationCents,
        rtpBasisPoints:       values.rtpBasisPoints ?? null,
        ownershipKind:        values.ownershipKind,
        ownerSupplierId:      values.ownerSupplierId || null,
        ownerSiteOwnerUserId: values.ownerSiteOwnerUserId || null,
        cashlessMode:         values.cashlessMode,
      });
    }
  };

  return (
    <EntityFormDialog<MachineFormValues>
      open={open}
      mode={mode}
      title={mode === 'create' ? 'Create machine' : 'Edit machine'}
      schema={schema}
      initialValues={initial}
      onSubmit={submit}
      onClose={onClose}
    >
      <Fields mode={mode} isArms={isArms} myOperatorId={me.data?.operator_id ?? null} />
    </EntityFormDialog>
  );
}

function Fields({ mode, isArms, myOperatorId }: {
  mode: 'create' | 'edit';
  isArms: boolean;
  myOperatorId: string | null;
}) {
  const { register, control, watch, formState: { errors } } = useFormContext<MachineFormValues>();
  const operatorId  = watch('operatorId');
  const ownership   = watch('ownershipKind');
  const effectiveOperatorId = isArms ? operatorId : myOperatorId;

  const operators = useOperatorsList();
  const operatorsQ = useQuery({
    queryKey: operators.queryKey,
    queryFn: operators.listFn,
    enabled: isArms && mode === 'create',
  });

  const sites = useSitesList(effectiveOperatorId || null);
  const sitesQ = useQuery({
    queryKey: sites.queryKey,
    queryFn: sites.listFn,
    enabled: !!effectiveOperatorId,
  });

  const suppliers = useSuppliersList();
  const suppliersQ = useQuery({
    queryKey: suppliers.queryKey,
    queryFn: suppliers.listFn,
    enabled: ownership === 'supplier',
  });

  const users = useUsersList(effectiveOperatorId || null);
  const usersQ = useQuery({
    queryKey: users.queryKey,
    queryFn: users.listFn,
    enabled: ownership === 'site_owner' && !!effectiveOperatorId,
  });

  const operatorOptions = useMemo(() => operatorsQ.data ?? [], [operatorsQ.data]);
  const siteOptions     = useMemo(() => selectableSites(sitesQ.data), [sitesQ.data]);
  const supplierOptions = useMemo(() =>
    (suppliersQ.data ?? []).filter((s) => s.isActive), [suppliersQ.data]);
  const userOptions     = useMemo(() => usersQ.data ?? [],     [usersQ.data]);

  return (
    <>
      {isArms && mode === 'create' && (
        <Controller
          name="operatorId"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select
              label="Operator"
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message ?? 'Tenant the machine belongs to.'}
              fullWidth
              {...field}
              value={field.value ?? ''}
            >
              <MenuItem value="">— select —</MenuItem>
              {operatorOptions.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.displayName} ({op.code})
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}

      <Controller
        name="siteId"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            select
            label="Site"
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? (
              effectiveOperatorId ? 'Where the machine lives.' : 'Pick an operator first.')}
            fullWidth
            disabled={!effectiveOperatorId}
            {...field}
            value={field.value ?? ''}
          >
            <MenuItem value="">— select —</MenuItem>
            {siteOptions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.displayName} — {s.code}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Asset number"
          size="small"
          disabled={mode === 'edit'}
          helperText={mode === 'edit' ? 'Asset number is immutable.' : (errors.assetNumber?.message ?? 'Operator-unique.')}
          error={!!errors.assetNumber}
          sx={{ flex: 1 }}
          autoFocus={mode === 'create'}
          {...register('assetNumber')}
        />
        <TextField
          label="Make"
          size="small"
          helperText={errors.make?.message}
          error={!!errors.make}
          sx={{ flex: 1 }}
          {...register('make')}
        />
        <TextField
          label="Model"
          size="small"
          helperText={errors.model?.message}
          error={!!errors.model}
          sx={{ flex: 1 }}
          {...register('model')}
        />
      </Stack>

      <TextField
        label="Game name (optional)"
        size="small"
        helperText={errors.gameName?.message}
        error={!!errors.gameName}
        fullWidth
        {...register('gameName')}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Controller
          name="protocol"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select
              label="Protocol"
              size="small"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ flex: 1 }}
              {...field}
            >
              {PROTOCOLS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>
          )}
        />
        <TextField
          label="Denomination (cents)"
          size="small"
          type="number"
          inputProps={{ min: 1 }}
          helperText={errors.denominationCents?.message ?? 'e.g. 1, 5, 25, 100'}
          error={!!errors.denominationCents}
          sx={{ flex: 1 }}
          {...register('denominationCents')}
        />
        <TextField
          label="RTP (basis points)"
          size="small"
          type="number"
          inputProps={{ min: 0, max: 10000 }}
          helperText={errors.rtpBasisPoints?.message ?? 'Optional. 9000 = 90.00%'}
          error={!!errors.rtpBasisPoints}
          sx={{ flex: 1 }}
          {...register('rtpBasisPoints')}
        />
      </Stack>

      {mode === 'create' && (
        <>
          <Typography variant="overline" color="text.secondary">Ownership</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="ownershipKind"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  label="Owner type"
                  size="small"
                  sx={{ flex: 1 }}
                  helperText="Operator-owned by default; switch to supplier or site owner if relevant."
                  {...field}
                >
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="supplier">Supplier</MenuItem>
                  <MenuItem value="site_owner">Site owner</MenuItem>
                </TextField>
              )}
            />
            {ownership === 'supplier' && (
              <Controller
                name="ownerSupplierId"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    label="Supplier"
                    size="small"
                    sx={{ flex: 2 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...field}
                    value={field.value ?? ''}
                  >
                    <MenuItem value="">— select —</MenuItem>
                    {supplierOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.displayName} ({s.code})
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}
            {ownership === 'site_owner' && (
              <Controller
                name="ownerSiteOwnerUserId"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    label="Site owner (user)"
                    size="small"
                    sx={{ flex: 2 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    {...field}
                    value={field.value ?? ''}
                  >
                    <MenuItem value="">— select —</MenuItem>
                    {userOptions.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.displayName} ({u.email})
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            )}
          </Stack>
        </>
      )}

      <Controller
        name="cashlessMode"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            select
            label="Cashless mode"
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            fullWidth
            {...field}
          >
            {CASHLESS_MODES.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
        )}
      />
    </>
  );
}
